import {
  CAROUSEL_PALETTE_IDS,
  CAROUSEL_PALETTES,
  COASTER_PALETTE_IDS,
  COASTER_PALETTES,
  isCarouselPaletteId,
  isCoasterPaletteId,
  type CarouselPaletteId,
  type CoasterPaletteId,
  type PalettePreset,
} from '../spikes/threejs/palettes';
import type { ActionResult, EntityAppearance, EntityKind, ParkState } from '../types';

export const DEFAULT_CAROUSEL_PALETTE_ID: CarouselPaletteId = 'candyStripe';
export const DEFAULT_COASTER_PALETTE_ID: CoasterPaletteId = 'classicRed';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function supportedPaletteIds(kind: EntityKind): readonly string[] | null {
  if (kind === 'starter_ride') {
    return CAROUSEL_PALETTE_IDS;
  }
  if (kind === 'roller_coaster') {
    return COASTER_PALETTE_IDS;
  }
  return null;
}

export function isApprovedPaletteId(kind: EntityKind, paletteId: string): boolean {
  if (kind === 'starter_ride') {
    return isCarouselPaletteId(paletteId);
  }
  if (kind === 'roller_coaster') {
    return isCoasterPaletteId(paletteId);
  }
  return false;
}

export function defaultPaletteId(kind: EntityKind): string | undefined {
  if (kind === 'starter_ride') {
    return DEFAULT_CAROUSEL_PALETTE_ID;
  }
  if (kind === 'roller_coaster') {
    return DEFAULT_COASTER_PALETTE_ID;
  }
  return undefined;
}

export function resolveApprovedPaletteId(kind: EntityKind, paletteId?: string): string | undefined {
  if (paletteId && isApprovedPaletteId(kind, paletteId)) {
    return paletteId;
  }
  return defaultPaletteId(kind);
}

export function approvedPalettePreset(
  kind: EntityKind,
  paletteId?: string,
): PalettePreset<string> | undefined {
  const resolved = resolveApprovedPaletteId(kind, paletteId);
  if (kind === 'starter_ride' && resolved && isCarouselPaletteId(resolved)) {
    return CAROUSEL_PALETTES[resolved];
  }
  if (kind === 'roller_coaster' && resolved && isCoasterPaletteId(resolved)) {
    return COASTER_PALETTES[resolved];
  }
  return undefined;
}

/** Old or invalid appearance metadata is dropped; the entity itself stays valid. */
export function parseEntityAppearance(kind: EntityKind, value: unknown): EntityAppearance | undefined {
  if (!isRecord(value) || typeof value.paletteId !== 'string') {
    return undefined;
  }
  if (!isApprovedPaletteId(kind, value.paletteId)) {
    return undefined;
  }
  return { paletteId: value.paletteId };
}

export function setEntityPalette(state: ParkState, entityId: string, paletteId: string): ActionResult {
  const entity = state.entities.find((item) => item.id === entityId);
  if (!entity) {
    return { ok: false, reason: 'empty' };
  }
  if (!isApprovedPaletteId(entity.kind, paletteId)) {
    return { ok: false, reason: 'invalid_palette' };
  }
  entity.appearance = { paletteId };
  return { ok: true };
}
